import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface CompanyPhoneNumber {
  id: string;
  phone_number: string;
  label: string;
  description: string | null;
  business_unit: string | null;
  usage_type: string;
  is_main: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface TwilioAccountNumber {
  phoneNumber: string;
  friendlyName: string;
  capabilities: { voice?: boolean; sms?: boolean; SMS?: boolean; mms?: boolean; MMS?: boolean };
}

export interface UserNumberAssignment {
  phone_number: string;
  user_id: string;
  user_name: string;
}

const COMPANY_KEY = ['company-phone-numbers'];

export function useCompanyPhoneNumbers() {
  return useQuery({
    queryKey: COMPANY_KEY,
    queryFn: async (): Promise<CompanyPhoneNumber[]> => {
      const { data, error } = await supabase
        .from('company_phone_numbers')
        .select('*')
        .order('is_main', { ascending: false })
        .order('phone_number', { ascending: true });

      if (error) throw error;
      return (data ?? []) as CompanyPhoneNumber[];
    },
    staleTime: 60_000,
  });
}

/** Números existentes na conta Twilio da empresa. */
export function useTwilioAccountNumbers() {
  return useQuery({
    queryKey: ['twilio-account-numbers'],
    queryFn: async (): Promise<TwilioAccountNumber[]> => {
      const { data, error } = await supabase.functions.invoke('twilio-list-numbers');
      if (error) throw error;
      return (data?.phoneNumbers ?? []) as TwilioAccountNumber[];
    },
    staleTime: 5 * 60_000,
    retry: 1,
  });
}

/** Números já atrelados a colaboradores (números pessoais). */
export function useUserNumberAssignments() {
  return useQuery({
    queryKey: ['twilio-user-assignments'],
    queryFn: async (): Promise<UserNumberAssignment[]> => {
      const { data, error } = await supabase
        .from('app_users')
        .select('id, twilio_phone_number, display_name, first_name, last_name')
        .not('twilio_phone_number', 'is', null);

      if (error) throw error;

      return (data ?? []).map((u) => ({
        phone_number: u.twilio_phone_number as string,
        user_id: u.id,
        user_name:
          u.display_name ||
          `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() ||
          'Usuário sem nome',
      }));
    },
    staleTime: 60_000,
  });
}

export function useCompanyPhoneNumberMutations() {
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: COMPANY_KEY });

  const registerNumber = useMutation({
    mutationFn: async (payload: {
      phone_number: string;
      label: string;
      business_unit?: string | null;
      usage_type?: string;
      description?: string | null;
    }) => {
      const { error } = await supabase.from('company_phone_numbers').insert({
        phone_number: payload.phone_number,
        label: payload.label,
        business_unit: payload.business_unit ?? null,
        usage_type: payload.usage_type ?? 'voice',
        description: payload.description ?? null,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Número registrado como número da empresa');
      invalidate();
    },
    onError: (error: Error) => toast.error(`Erro ao registrar número: ${error.message}`),
  });

  const updateNumber = useMutation({
    mutationFn: async ({ id, ...updates }: Partial<CompanyPhoneNumber> & { id: string }) => {
      const { error } = await supabase
        .from('company_phone_numbers')
        .update(updates)
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Número atualizado');
      invalidate();
    },
    onError: (error: Error) => toast.error(`Erro ao atualizar número: ${error.message}`),
  });

  const removeNumber = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('company_phone_numbers').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Número removido da central');
      invalidate();
    },
    onError: (error: Error) => toast.error(`Erro ao remover número: ${error.message}`),
  });

  /** Marca um número como principal, desmarcando o anterior. */
  const setMainNumber = useMutation({
    mutationFn: async (id: string) => {
      const { error: clearError } = await supabase
        .from('company_phone_numbers')
        .update({ is_main: false })
        .eq('is_main', true)
        .neq('id', id);
      if (clearError) throw clearError;

      const { error } = await supabase
        .from('company_phone_numbers')
        .update({ is_main: true, is_active: true })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Número principal da empresa definido');
      invalidate();
    },
    onError: (error: Error) => toast.error(`Erro ao definir número principal: ${error.message}`),
  });

  return { registerNumber, updateNumber, removeNumber, setMainNumber };
}
